import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../utils/api'
import AdminLayout from '../components/AdminLayout';

interface Story {
  id: number;
  title: string;
  completedAt: string;
}

interface PublicationStories {
  publication: {
    id: number;
    name: string;
    avatarUrl?: string;
  };
  stories: Story[];
}

interface UserBasic {
  id: string;
  fullName: string;
  email: string;
}

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 16px;
  color: #6b7280;
  a { color: #00a6c2; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 24px 0;
`;

const TabNav = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 24px;
`;

const TabLink = styled(Link)<{ $active?: boolean }>`
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ $active }) => ($active ? '#00a6c2' : '#6b7280')};
  text-decoration: none;
  border-bottom: 2px solid ${({ $active }) => ($active ? '#00a6c2' : 'transparent')};
  margin-bottom: -1px;
  &:hover { color: #00a6c2; background: #f9fafb; }
`;

const Section = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 24px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 20px 0;
`;

const PublicationGroup = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 16px;
  overflow: hidden;
`;

const PublicationHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
`;

const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  object-fit: cover;
  margin-right: 12px;
`;

const PlaceholderAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  color: #9ca3af;
  font-size: 12px;
`;

const PubName = styled(Link)`
  color: #00a6c2;
  text-decoration: none;
  font-weight: 500;
  &:hover { text-decoration: underline; }
`;

const StoryList = styled.div`
  padding: 0;
`;

const StoryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: white;
  &:last-child { border-bottom: none; }
`;

const StoryLink = styled.a`
  color: #00a6c2;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const StoryDate = styled.span`
  color: #6b7280;
  font-size: 13px;
`;

const ExternalIcon = styled.span`
  margin-left: 4px;
  font-size: 12px;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 24px;
  color: #6b7280;
`;

function AdminUserStoriesPage() {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<UserBasic | null>(null);
  const [storiesByPub, setStoriesByPub] = useState<PublicationStories[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [userRes, storiesRes] = await Promise.all([
        api.get(`/admin2/users/${id}.json`),
        api.get(`/admin2/users/${id}/stories.json`)
      ]);
      setUser({
        id: userRes.data.id,
        fullName: `${userRes.data.firstName || ''} ${userRes.data.lastName || ''}`.trim() || userRes.data.email,
        email: userRes.data.email
      });
      setStoriesByPub(storiesRes.data.storiesByPublication || []);
    } catch (err) {
      console.error('Failed to load data', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <AdminLayout title="Users" pageTitle="User Stories">
        <LoadingState>Loading...</LoadingState>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Users" pageTitle={`${user?.fullName || 'User'} - Stories`}>
      <Breadcrumb>
        <Link to="/admin2/users">Users</Link>
        <span>›</span>
        <Link to={`/admin2/users/${id}/edit`}>{user?.fullName} ({user?.email})</Link>
        <span>›</span>
        <span>Stories</span>
      </Breadcrumb>

      <PageTitle>{user?.fullName}</PageTitle>

      <TabNav>
        <TabLink to={`/admin2/users/${id}/edit`}>User info</TabLink>
        <TabLink to={`/admin2/users/${id}/publications`}>Publications</TabLink>
        <TabLink to={`/admin2/users/${id}/stories`} $active>Stories</TabLink>
        <TabLink to={`/admin2/users/${id}/audit_events`}>Audit Events</TabLink>
        <TabLink to={`/admin2/users/${id}/content_origin_access`}>Content Origin Access</TabLink>
      </TabNav>

      <Section>
        <SectionTitle>Stories: paid and submitted</SectionTitle>
        
        {storiesByPub.length === 0 ? (
          <EmptyState>No stories found for this user.</EmptyState>
        ) : (
          storiesByPub.map((group, idx) => (
            <PublicationGroup key={idx}>
              <PublicationHeader>
                {group.publication.avatarUrl ? (
                  <Avatar src={group.publication.avatarUrl} alt={group.publication.name} />
                ) : (
                  <PlaceholderAvatar>📄</PlaceholderAvatar>
                )}
                <PubName to={`/admin2/publications/${group.publication.id}/edit`}>
                  {group.publication.name}
                </PubName>
              </PublicationHeader>
              <StoryList>
                {group.stories.map((story) => (
                  <StoryItem key={story.id}>
                    <StoryLink href={`/stories/${story.id}`} target="_blank" rel="noopener noreferrer">
                      {story.title}
                      <ExternalIcon>↗</ExternalIcon>
                    </StoryLink>
                    <StoryDate>{story.completedAt}</StoryDate>
                  </StoryItem>
                ))}
              </StoryList>
            </PublicationGroup>
          ))
        )}
      </Section>
    </AdminLayout>
  );
}

export default AdminUserStoriesPage;
